import { BlockLayoutType, ServerBlockTypeType } from "@pathwright/blocks-core"
import { useEditorStateSelector } from "@pathwright/blocks-engine"
import Button from "@pathwright/ui/src/components/button/Button"
import useLocalStorage from "@pathwright/ui/src/components/hooks/useLocalStorage"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import View from "@pathwright/ui/src/components/ui/View"
import { cloneDeep } from "lodash"
import React, { useEffect, useMemo, useRef } from "react"
import styled from "styled-components"
import BlockOption from "./BlockOption"
import { filterDeprecatedMenuOption, mapLayoutToMenuOption } from "./utils"

export const baseMenuData = {
  title: {
    label: "Title",
    icon: "text-cursor",
    options: []
  },
  text: {
    label: "Text",
    icon: "text-edit",
    options: []
  },
  quote: {
    label: "Quote",
    icon: "quote-marks",
    options: []
  },
  image: {
    label: "Image",
    icon: "image",
    options: []
  },
  media: {
    label: "Media",
    icon: "play",
    options: []
  },
  question: {
    label: "Question",
    icon: "check-dash-square",
    options: []
  },
  task: {
    label: "Task",
    icon: "check-list",
    options: []
  },
  interactive: {
    label: "Interactive",
    icon: "drag-drop",
    options: []
  },
  gallery: {
    label: "Gallery",
    icon: "grid-large",
    options: []
  },
  divider: {
    label: "Divider",
    icon: "arrow-double-vertical",
    options: []
  }
}

const buildMenuData = (blockTypes: ServerBlockTypeType[]) => {
  const menuData = cloneDeep(baseMenuData)

  // 1. Get all layouts as menu options
  //  filter out deprecated layouts
  const layoutsAsMenuOptions = blockTypes
    .filter((bt) => !!bt.menu)
    .reduce((lyts, bt) => {
      const layouts = Object.values(bt.layouts)
      return lyts.concat(
        layouts.map(mapLayoutToMenuOption(bt)) as unknown as BlockLayoutType[]
      )
    }, [] as BlockLayoutType[])
    .filter(
      filterDeprecatedMenuOption as unknown as (
        value: BlockLayoutType
      ) => boolean
    )

  // 2. Map menu options to menu categories
  const menuOptions = layoutsAsMenuOptions.reduce(
    (
      nextMenuData: Record<
        string,
        { label: string; icon: string; options: BlockLayoutType[] }
      >,
      option: BlockLayoutType
    ) => {
      const category =
        nextMenuData[option.menuCategory as keyof typeof nextMenuData]
      if (!category) return nextMenuData

      category.options.push(option)

      return nextMenuData
    },
    { ...menuData }
  )

  // 3. Sort menu categories
  Object.values(menuOptions).forEach((category) => {
    category.options = category.options.sort((a, b) =>
      (a.menuOrder ?? 0) < (b.menuOrder ?? 0) ? -1 : 1
    )
  })

  // Don't use a menu item when it has no block types in it
  return Object.values(menuOptions).filter((m) => !!m.options.length)
}

const BlockSelectorWrap = styled(View.Tertiary)`
  position: relative;
  padding: 20px 0px;
  width: 100%;
  margin: 0px auto;
  background-color: #efefef !important;
`

const SideScroll = styled.div`
  overflow: hidden;
  overflow-x: auto;
  display: flex;
  width: 100%;
  margin: 0 auto;
  backface-visibility: hidden;
  &::-webkit-scrollbar {
    /* width: 0;
    height: 0;
    background: transparent; */
  }
`

const BlockTypes = styled.div`
  display: flex;
  align-items: center;
  margin: 10px auto;
  padding: 0 20px;
`

const BlockOptions = styled.div`
  display: flex;
  align-items: center;
  margin: 0 auto;
  padding: 0 10px;

  .SubmitView {
    width: 80%;
  }
`

const lskey = "blockSelector-selectedIndex"

const BlockSelector = React.memo(
  ({
    onSelect
  }: {
    onSelect: (
      blockType: ServerBlockTypeType["type"],
      data: Record<string, any>,
      layout: BlockLayoutType["key"]
    ) => void
  }) => {
    const blockTypes = useEditorStateSelector((state) => state.blockTypes)

    const menuData = useMemo(() => buildMenuData(blockTypes), [blockTypes])

    const [selectedIndex, setSelectedIndex] = useLocalStorage(
      lskey,
      // Cap the initial selectedIndex to the max options included in menuData.
      // This prevents a value in localStorage that is outside the current range of menuData
      // from breaking the UI.
      (v: number | null) => (v !== null ? Math.min(v, menuData.length - 1) : 0)
    )

    // Return options container scroll position to the left after selecting
    // different block type.
    const optionsScrollerRef = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
      if (!optionsScrollerRef.current) return
      optionsScrollerRef.current.scrollLeft = 0
    }, [selectedIndex, optionsScrollerRef])

    // Get current block options based on currect selectedIndex.
    const blockOptions = useMemo(
      () => menuData[selectedIndex].options,
      [menuData, selectedIndex]
    )

    return (
      // stop touch move propagation to prevent step slider from activating
      <BlockSelectorWrap className="BlockSelector swiper-no-swiping">
        <SideScroll>
          <BlockTypes>
            {menuData.map((category, i) => {
              const selected = i === selectedIndex
              return (
                <Button
                  key={`category-${i}`}
                  styleType={selected ? "secondary" : "tertiary"}
                  onClick={() => setSelectedIndex(i)}
                  className={`BlockSelector__category BlockSelector__category--${category.label}`}
                  blockIndex="0"
                >
                  <Pathicon
                    icon={category.icon}
                    style={{
                      marginRight: "4px",
                      marginTop: "1px",
                      color: "rgba(0,0,0,0.6)",
                      fontSize: "0.9em"
                    }}
                  />
                  {category.label}
                </Button>
              )
            })}
          </BlockTypes>
        </SideScroll>
        <SideScroll ref={optionsScrollerRef}>
          <BlockOptions className="BlockSelector__BlockOptions">
            {blockOptions.map((option, i) => {
              const { key, blockType, key: layout, ...props } = option
              return (
                <BlockOption
                  key={`block-option-${key}-${i}`}
                  // Some blocks (even as previews) may want to have a unique id
                  onSelect={onSelect}
                  blockType={blockType!}
                  layout={layout}
                  {...props}
                />
              )
            })}
          </BlockOptions>
        </SideScroll>
      </BlockSelectorWrap>
    )
  }
)

export default BlockSelector
